import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const InstallationFormModal = ({ isOpen, onClose, onSubmit }) => {
  // Form state
  const [formData, setFormData] = useState({
    store: '',
    brandingMedium: '',
    brandingId: '',
    kvId: '',
    status: 'Planned',
    image: null
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Mock data - in real app these would come from APIs/database
  const mockStores = [
    { value: 'DHA', label: 'Dhaka Central Mall', code: 'DHA', location: 'Gulshan-2, Dhaka' },
    { value: 'CTG', label: 'Chittagong Plaza', code: 'CTG', location: 'Agrabad, Chittagong' },
    { value: 'SYL', label: 'Sylhet Shopping Center', code: 'SYL', location: 'Zindabazar, Sylhet' },
    { value: 'RAJ', label: 'Rajshahi Grand Mall', code: 'RAJ', location: 'Shaheb Bazar, Rajshahi' },
    { value: 'BAR', label: 'Barisal City Center', code: 'BAR', location: 'Band Road, Barisal' },
    { value: 'KHU', label: 'Khulna Trade Center', code: 'KHU', location: 'Khalishpur, Khulna' }
  ];

  // Mock branding mediums per store
  const mockBrandingMediums = {
    'DHA': [
      { value: 'Billboard', label: 'Billboard' },
      { value: 'LED Screen', label: 'LED Screen' },
      { value: 'Window Display', label: 'Window Display' },
      { value: 'In-Store Static', label: 'In-Store Static' }
    ],
    'CTG': [
      { value: 'Gondola Branding', label: 'Gondola Branding' },
      { value: 'In-Store Static', label: 'In-Store Static' },
      { value: 'FSU', label: 'FSU' },
      { value: 'Window Display', label: 'Window Display' }
    ],
    'SYL': [
      { value: 'LED Screen', label: 'LED Screen' },
      { value: 'Billboard', label: 'Billboard' },
      { value: 'In-Store Static', label: 'In-Store Static' }
    ],
    'RAJ': [
      { value: 'Window Display', label: 'Window Display' },
      { value: 'In-Store Static', label: 'In-Store Static' },
      { value: 'FSU', label: 'FSU' }
    ],
    'BAR': [
      { value: 'In-Store Static', label: 'In-Store Static' },
      { value: 'Gondola Branding', label: 'Gondola Branding' },
      { value: 'FSU', label: 'FSU' }
    ],
    'KHU': [
      { value: 'Billboard', label: 'Billboard' },
      { value: 'LED Screen', label: 'LED Screen' },
      { value: 'Window Display', label: 'Window Display' }
    ]
  };

  // Mock existing branding IDs (created in Cost Calculator)
  const mockBrandingIds = {
    'DHA-Billboard': [
      { value: 'BB-DHA-LIS-001', label: 'BB-DHA-LIS-001', brand: 'Lily Skincare', medium: 'Billboard' },
      { value: 'BB-DHA-SAM-002', label: 'BB-DHA-SAM-002', brand: 'Samsung', medium: 'Billboard' }
    ],
    'DHA-LED Screen': [
      { value: 'LS-DHA-CAV-003', label: 'LS-DHA-CAV-003', brand: 'Cavotin', medium: 'LED Screen' },
      { value: 'LS-DHA-VIV-004', label: 'LS-DHA-VIV-004', brand: 'Viva', medium: 'LED Screen' }
    ],
    'DHA-Window Display': [
      { value: 'WD-DHA-PON-005', label: 'WD-DHA-PON-005', brand: 'Ponds', medium: 'Window Display' }
    ],
    'DHA-In-Store Static': [
      { value: 'IS-DHA-UNI-006', label: 'IS-DHA-UNI-006', brand: 'Unilever', medium: 'In-Store Static' }
    ],
    'CTG-Gondola Branding': [
      { value: 'GB-CTG-CAV-007', label: 'GB-CTG-CAV-007', brand: 'Cavotin Haircare', medium: 'Gondola Branding' },
      { value: 'GB-CTG-ORI-008', label: 'GB-CTG-ORI-008', brand: 'Orix', medium: 'Gondola Branding' }
    ],
    'CTG-In-Store Static': [
      { value: 'IS-CTG-LIS-009', label: 'IS-CTG-LIS-009', brand: 'Lily', medium: 'In-Store Static' }
    ],
    'SYL-LED Screen': [
      { value: 'LS-SYL-SAM-010', label: 'LS-SYL-SAM-010', brand: 'Samsung', medium: 'LED Screen' }
    ],
    'RAJ-Window Display': [
      { value: 'WD-RAJ-PON-011', label: 'WD-RAJ-PON-011', brand: 'Ponds', medium: 'Window Display' }
    ],
    'BAR-In-Store Static': [
      { value: 'IS-BAR-UNI-012', label: 'IS-BAR-UNI-012', brand: 'Unilever', medium: 'In-Store Static' }
    ]
  };

  // Mock Key Visuals from Key Visual Management (filtered by branding medium)
  const mockKeyVisuals = {
    'Billboard': [
      { 
        value: 'LIS-SC-001', 
        label: 'LIS-SC-001 - Lily Skincare Summer Glow', 
        brand: 'Lily',
        thumbnailUrl: 'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?w=100&h=80&fit=crop&crop=faces'
      },
      { 
        value: 'ELT-WL-001', 
        label: 'ELT-WL-001 - Elite Wellness Premium', 
        brand: 'Elite',
        thumbnailUrl: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=100&h=80&fit=crop&crop=faces'
      }
    ],
    'LED Screen': [
      { 
        value: 'CAV-HC-001', 
        label: 'CAV-HC-001 - Cavotin Hair Transformation', 
        brand: 'Cavotin',
        thumbnailUrl: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=100&h=80&fit=crop&crop=faces',
        isVideo: true,
        duration: '0:45'
      },
      { 
        value: 'VIV-PC-001', 
        label: 'VIV-PC-001 - Viva Modern Lifestyle', 
        brand: 'Viva',
        thumbnailUrl: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=100&h=80&fit=crop&crop=faces',
        isVideo: true,
        duration: '0:30'
      }
    ],
    'Gondola Branding': [
      { 
        value: 'ORI-HFC-001', 
        label: 'ORI-HFC-001 - Orix Complete Solutions', 
        brand: 'Orix',
        thumbnailUrl: 'https://images.unsplash.com/photo-1585515656853-c6b58a6ca2a5?w=100&h=80&fit=crop&crop=faces'
      }
    ],
    'Window Display': [
      { 
        value: 'SPK-CC-001', 
        label: 'SPK-CC-001 - Sparkle Youth Colors', 
        brand: 'Sparkle',
        thumbnailUrl: 'https://images.unsplash.com/photo-1616683693504-3ea7e9ad6fec?w=100&h=80&fit=crop&crop=faces'
      }
    ],
    'In-Store Static': [
      { 
        value: 'CAV-HC-003', 
        label: 'CAV-HC-003 - Cavotin Hair Repair', 
        brand: 'Cavotin',
        thumbnailUrl: 'https://images.unsplash.com/photo-1559599101-f09722fb4948?w=100&h=80&fit=crop&crop=faces'
      }
    ],
    'FSU': [
      { 
        value: 'ORI-HFC-002', 
        label: 'ORI-HFC-002 - Orix Fabric Care', 
        brand: 'Orix',
        thumbnailUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=80&fit=crop&crop=faces'
      }
    ]
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        store: '',
        brandingMedium: '',
        brandingId: '',
        kvId: '',
        status: 'Planned',
        image: null
      });
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Get filtered options based on selections
  const availableBrandingMediums = formData?.store ? 
    mockBrandingMediums?.[formData?.store] || [] : [];

  const availableBrandingIds = (formData?.store && formData?.brandingMedium) ? 
    mockBrandingIds?.[`${formData?.store}-${formData?.brandingMedium}`] || [] : [];

  const availableKeyVisuals = formData?.brandingMedium ? 
    mockKeyVisuals?.[formData?.brandingMedium] || [] : [];

  const selectedStore = mockStores?.find(store => store?.value === formData?.store);
  const selectedKV = availableKeyVisuals?.find(kv => kv?.value === formData?.kvId);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Reset dependent fields when parent changes
      if (field === 'store') {
        updated.brandingMedium = '';
        updated.brandingId = '';
        updated.kvId = '';
      } else if (field === 'brandingMedium') {
        updated.brandingId = '';
        updated.kvId = '';
      } else if (field === 'store' || field === 'brandingMedium') {
        updated.kvId = '';
      }
      
      return updated;
    });

    // Clear related errors
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData?.store) newErrors.store = 'Store selection is required';
    if (!formData?.brandingMedium) newErrors.brandingMedium = 'Branding Medium selection is required';
    if (!formData?.brandingId) newErrors.brandingId = 'Branding ID selection is required';
    if (!formData?.kvId) newErrors.kvId = 'KV ID selection is required';

    // Check if branding ID exists for selected store + medium
    if (formData?.store && formData?.brandingMedium && availableBrandingIds?.length === 0) {
      newErrors.brandingId = '⚠ No Branding Item exists for this Store and Medium. Please create one in Cost Calculator first.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          image: {
            file,
            preview: e?.target?.result,
            name: file?.name,
            size: file?.size
          }
        }));
      };
      reader?.readAsDataURL(file);
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Prepare installation data
      const installationData = {
        id: `INST_${Date.now()}`,
        storeName: selectedStore?.label || 'Unknown Store',
        storeCode: formData?.store,
        storeLocation: selectedStore?.location || 'Location TBD',
        brandingType: formData?.brandingMedium,
        brandingId: formData?.brandingId,
        kvId: formData?.kvId,
        status: 'planned',
        installationDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0], // 7 days from now
        createdOn: new Date()?.toISOString(),
        assignedTo: 'Store Manager',
        teamName: 'Store Team',
        teamLead: 'Store Manager',
        totalCost: 0, // Will be updated from Cost Calculator
        quantity: 1,
        notes: `Manual installation entry created via form`,
        photos: formData?.image ? [{
          id: `PHOTO_${Date.now()}`,
          url: formData?.image?.preview,
          thumbnail: formData?.image?.preview,
          caption: 'Initial upload',
          uploadedAt: new Date()?.toISOString(),
          fileName: formData?.image?.name,
          fileSize: formData?.image?.size
        }] : [],
        auditLog: [{
          id: `AUDIT_${Date.now()}`,
          action: 'Installation Created',
          user: 'Current User',
          timestamp: new Date()?.toISOString(),
          details: 'Installation created manually via form',
          oldValue: null,
          newValue: 'planned'
        }],
        timeline: [{
          title: 'Installation Planned',
          description: `New installation created for ${formData?.brandingMedium}`,
          date: new Date()?.toISOString()?.split('T')?.[0],
          icon: 'Calendar'
        }]
      };

      onSubmit(installationData);
      onClose();

    } catch (error) {
      console.error('Installation creation failed:', error);
      alert('Failed to create installation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">New Installation</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Create a new installation tracking entry
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="p-6 space-y-6">
            {/* Store Selection */}
            <div>
              <Select
                label="Store"
                placeholder="Select a store"
                required
                options={mockStores}
                value={formData?.store}
                onChange={(value) => handleFieldChange('store', value)}
                error={errors?.store}
                searchable
              />
              {selectedStore && (
                <p className="text-sm text-muted-foreground mt-2">
                  <Icon name="MapPin" size={14} className="inline mr-1" />
                  {selectedStore?.location}
                </p>
              )}
            </div>

            {/* Branding Medium Selection */}
            <div>
              <Select
                label="Branding Medium"
                placeholder="Select branding medium"
                required
                options={availableBrandingMediums}
                value={formData?.brandingMedium}
                onChange={(value) => handleFieldChange('brandingMedium', value)}
                error={errors?.brandingMedium}
                disabled={!formData?.store}
                description={!formData?.store ? "Select a store first" : `${availableBrandingMediums?.length} mediums available for this store`}
              />
            </div>

            {/* Branding ID Selection */}
            <div>
              <Select
                label="Branding ID"
                placeholder="Select existing branding ID"
                required
                options={availableBrandingIds}
                value={formData?.brandingId}
                onChange={(value) => handleFieldChange('brandingId', value)}
                error={errors?.brandingId}
                disabled={!formData?.brandingMedium}
                description={!formData?.brandingMedium ? "Select a branding medium first" : 
                  availableBrandingIds?.length === 0 ? 
                    "⚠ No branding items found. Create one in Cost Calculator first." : 
                    `${availableBrandingIds?.length} existing branding items available`}
              />
            </div>

            {/* KV ID Selection with Preview */}
            <div>
              <Select
                label="KV ID"
                placeholder="Select key visual"
                required
                options={availableKeyVisuals?.map(kv => ({
                  ...kv,
                  customRender: (
                    <div className="flex items-center gap-3 py-1">
                      <div className="w-12 h-10 rounded border border-border overflow-hidden flex-shrink-0 bg-muted flex items-center justify-center">
                        {kv?.thumbnailUrl ? (
                          <div className="relative w-full h-full">
                            <Image
                              src={kv?.thumbnailUrl}
                              alt={kv?.label}
                              className="w-full h-full object-cover"
                            />
                            {kv?.isVideo && (
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <Icon name="Play" size={12} className="text-white" />
                              </div>
                            )}
                          </div>
                        ) : (
                          <Icon name="ImageIcon" size={16} className="text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground truncate">
                          {kv?.value}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {kv?.brand}
                          {kv?.duration && ` • ${kv?.duration}`}
                        </div>
                      </div>
                    </div>
                  )
                }))}
                value={formData?.kvId}
                onChange={(value) => handleFieldChange('kvId', value)}
                error={errors?.kvId}
                disabled={!formData?.brandingMedium}
                description={!formData?.brandingMedium ? "Select a branding medium first" : 
                  `${availableKeyVisuals?.length} key visuals available for ${formData?.brandingMedium}`}
                searchable
              />

              {/* KV Preview */}
              {selectedKV && (
                <div className="mt-3 p-3 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-16 h-12 rounded border border-border overflow-hidden flex-shrink-0">
                      <div className="relative w-full h-full">
                        <Image
                          src={selectedKV?.thumbnailUrl}
                          alt={selectedKV?.label}
                          className="w-full h-full object-cover"
                        />
                        {selectedKV?.isVideo && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <Icon name="Play" size={14} className="text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{selectedKV?.value}</h4>
                      <p className="text-sm text-muted-foreground">
                        Brand: {selectedKV?.brand}
                        {selectedKV?.duration && ` • Duration: ${selectedKV?.duration}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Status (Read-only, defaults to Planned) */}
            <div>
              <Input
                label="Status"
                value="Planned"
                disabled
                description="New installations start with 'Planned' status"
              />
            </div>

            {/* Optional Image Upload */}
            <div>
              <label className="text-sm font-medium leading-none text-foreground block mb-2">
                Image Upload
                <span className="text-muted-foreground font-normal ml-1">(Optional - can be added later)</span>
              </label>
              
              <div className="space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/80"
                />
                
                {formData?.image && (
                  <div className="border border-border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-start gap-3">
                      <div className="w-16 h-16 rounded border border-border overflow-hidden flex-shrink-0">
                        <Image
                          src={formData?.image?.preview}
                          alt={formData?.image?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{formData?.image?.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {Math.round(formData?.image?.size / 1024)} KB
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFieldChange('image', null)}
                          className="text-destructive hover:text-destructive mt-1 p-0 h-auto"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="default"
              type="submit"
              disabled={loading}
              loading={loading}
            >
              <Icon name="Plus" size={16} />
              {loading ? 'Creating...' : 'Create Installation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstallationFormModal;